import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Ticket, Plus, Lightbulb, HelpCircle, BarChart3, Search,
  Loader2, Send, Paperclip, X, ChevronRight,
} from 'lucide-react'
import {
  useNdCatalogs, useMyTickets, useCreateTicket, useTicket, useReplyTicket,
  useMyImprovements, useFaq, useFaqSearch,
} from '@/hooks/useNaiDesk'
import type { NdTicket, NdImprovement } from '@/hooks/useNaiDesk'
import { sanitizeHtml, looksLikeHtml } from '@/lib/sanitize'

type Tab = 'overview' | 'new' | 'tickets' | 'melhorias' | 'faq'

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  forwarded: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  waiting_client: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  resolved: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}
const PRIORITY_COLORS: Record<string, string> = {
  Baixa: 'bg-emerald-50 text-emerald-700',
  'Média': 'bg-yellow-50 text-yellow-700',
  Alta: 'bg-orange-50 text-orange-700',
  'Crítica': 'bg-red-50 text-red-700',
}

export default function NaiDeskPage() {
  const [tab, setTab] = useState<Tab>('overview')

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Nai Desk</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Portal de atendimento, melhorias e FAQ</p>
      </header>

      <nav className="flex gap-1 border-b border-gray-200 dark:border-gray-800 mb-6 overflow-x-auto">
        <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={<BarChart3 className="h-4 w-4" />}>Visão Geral</TabBtn>
        <TabBtn active={tab === 'new'} onClick={() => setTab('new')} icon={<Plus className="h-4 w-4" />}>Novo Chamado</TabBtn>
        <TabBtn active={tab === 'tickets'} onClick={() => setTab('tickets')} icon={<Ticket className="h-4 w-4" />}>Meus Chamados</TabBtn>
        <TabBtn active={tab === 'melhorias'} onClick={() => setTab('melhorias')} icon={<Lightbulb className="h-4 w-4" />}>Melhorias</TabBtn>
        <TabBtn active={tab === 'faq'} onClick={() => setTab('faq')} icon={<HelpCircle className="h-4 w-4" />}>FAQ</TabBtn>
      </nav>

      {tab === 'overview' && <Overview />}
      {tab === 'new' && <NewTicket />}
      {tab === 'tickets' && <MyTickets />}
      {tab === 'melhorias' && <MyMelhorias />}
      {tab === 'faq' && <FaqTab />}

      <style>{`
        .nd-html { word-break: break-word; }
        .nd-html img { max-width: 100%; height: auto; border-radius: 4px; }
        .nd-html table { border-collapse: collapse; max-width: 100%; }
        .nd-html table td, .nd-html table th { border: 1px solid rgba(0,0,0,0.1); padding: 4px 8px; }
        .nd-html blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; margin: 8px 0; color: #64748b; }
        .nd-html p { margin: 6px 0; }
        .nd-html a { color: #2563eb; text-decoration: underline; }
        .dark .nd-html a { color: #60a5fa; }
        .nd-sdp-img-placeholder {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 10px; margin: 3px 2px;
          border: 1px dashed #cbd5e1; border-radius: 8px;
          background: #f8fafc; color: #475569 !important;
          text-decoration: none !important; font-size: 11.5px;
        }
        .nd-sdp-img-placeholder:hover { border-color: #2563eb; background: #eff6ff; color: #1e40af !important; }
        .dark .nd-sdp-img-placeholder { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.15); color: #94a3b8 !important; }
        .dark .nd-sdp-img-placeholder:hover { background: rgba(96,165,250,0.1); border-color: #60a5fa; color: #bfdbfe !important; }
      `}</style>
    </div>
  )
}

function TabBtn({ active, onClick, icon, children }:
  { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-blue-500 text-blue-700 dark:text-blue-400'
          : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
      }`}>
      {icon}{children}
    </button>
  )
}

// ── Overview ──
function Overview() {
  const { data: tickets } = useMyTickets({ per_page: 200 })
  const list = tickets || []
  const stats = {
    total: list.length,
    open: list.filter(t => t.status === 'open').length,
    in_progress: list.filter(t => t.status === 'in_progress').length,
    resolved: list.filter(t => ['resolved', 'closed'].includes(t.status)).length,
  }
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total de Chamados" value={stats.total} color="bg-blue-500" />
        <StatCard label="Abertos" value={stats.open} color="bg-emerald-500" />
        <StatCard label="Em Andamento" value={stats.in_progress} color="bg-yellow-500" />
        <StatCard label="Resolvidos" value={stats.resolved} color="bg-purple-500" />
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold mb-4">Últimos chamados</h3>
        {list.slice(0, 5).map(t => (
          <Link key={t.id} to="#" className="block py-3 border-b last:border-0 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-3 px-3 rounded">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-500">#{t.ticket_number}</span>
                  <StatusBadge status={t.status} />
                </div>
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{t.subject}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </div>
          </Link>
        ))}
        {list.length === 0 && <p className="text-sm text-gray-500 py-4">Nenhum chamado registrado.</p>}
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className={`w-10 h-1 rounded-full ${color} mb-3`} />
      <div className="text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</div>
    </div>
  )
}

// ── New Ticket ──
function NewTicket() {
  const { data: cat } = useNdCatalogs()
  const create = useCreateTicket()
  const [type, setType] = useState('')
  const [subtype, setSubtype] = useState('')
  const [moduleSel, setModule] = useState('')
  const [priority, setPriority] = useState('Média')
  const [tags, setTags] = useState<string[]>([])
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [success, setSuccess] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(false)
    const fd = new FormData()
    fd.append('type', type)
    fd.append('subtype', subtype)
    fd.append('module', moduleSel)
    fd.append('priority', priority)
    fd.append('tags', tags.join(','))
    fd.append('subject', subject)
    fd.append('description', description)
    files.forEach(f => fd.append('files', f))
    try {
      await create.mutateAsync(fd)
      setSuccess(true)
      setSubject(''); setDescription(''); setFiles([]); setTags([])
    } catch { /* error shown by mutation state */ }
  }

  return (
    <form onSubmit={onSubmit} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4 max-w-3xl">
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 px-4 py-3 rounded-lg text-sm">
          ✓ Chamado registrado com sucesso!
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Tipo *">
          <Select value={type} onChange={setType} options={cat?.types || []} required />
        </Field>
        <Field label="Subtipo">
          <Select value={subtype} onChange={setSubtype} options={cat?.subtypes || []} />
        </Field>
        <Field label="Módulo">
          <Select value={moduleSel} onChange={setModule} options={cat?.modules || []} />
        </Field>
      </div>

      <Field label="Prioridade">
        <div className="flex flex-wrap gap-2">
          {['Baixa', 'Média', 'Alta', 'Crítica'].map(p => (
            <button key={p} type="button" onClick={() => setPriority(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                priority === p
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400'
              }`}>{p}</button>
          ))}
        </div>
      </Field>

      <Field label="Tags">
        <div className="flex flex-wrap gap-2">
          {(cat?.tags || []).map(t => (
            <button key={t} type="button"
              onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                tags.includes(t)
                  ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-300'
              }`}>{t}</button>
          ))}
        </div>
      </Field>

      <Field label="Assunto *">
        <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={500}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
      </Field>

      <Field label="Descrição *">
        <textarea value={description} onChange={e => setDescription(e.target.value)} required rows={6}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
      </Field>

      <Field label="Anexos">
        <input type="file" multiple onChange={e => setFiles(Array.from(e.target.files || []))}
          className="text-sm text-gray-600 dark:text-gray-400" />
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <Paperclip className="h-3 w-3" />{f.name} ({(f.size / 1024).toFixed(0)} KB)
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700"><X className="h-3 w-3" /></button>
            </li>
          ))}
        </ul>
      </Field>

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={create.isPending || !type || !subject || !description}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
          {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Abrir chamado
        </button>
      </div>
    </form>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
      {children}
    </div>
  )
}

function Select({ value, onChange, options, required }:
  { value: string; onChange: (v: string) => void; options: string[]; required?: boolean }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} required={required}
      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm">
      <option value="">— selecione —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

// ── My Tickets ──
function MyTickets() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const { data: tickets, isLoading } = useMyTickets({ q, status })
  const [openId, setOpenId] = useState<string | null>(null)

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
          <option value="">Todos os status</option>
          <option value="open">Aberto</option>
          <option value="in_progress">Em Andamento</option>
          <option value="forwarded">Encaminhado</option>
          <option value="waiting_client">Aguardando Cliente</option>
          <option value="resolved">Resolvido</option>
          <option value="closed">Fechado</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Assunto</th>
              <th className="px-4 py-3 text-left">Tipo</th>
              <th className="px-4 py-3 text-left">Módulo</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Prioridade</th>
              <th className="px-4 py-3 text-left">Data</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {tickets?.map(t => (
              <tr key={t.id} onClick={() => setOpenId(t.id)} className="cursor-pointer border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3 font-mono text-gray-500">#{t.ticket_number}</td>
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 max-w-md truncate">{t.subject}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.type}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.module || '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</span></td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
              </tr>
            ))}
            {tickets && tickets.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Nenhum chamado encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      {openId && <TicketModal ticketId={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    open: 'Aberto', in_progress: 'Em Andamento', forwarded: 'Encaminhado',
    waiting_client: 'Aguardando', resolved: 'Resolvido', closed: 'Fechado',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{labels[status] || status}</span>
}

function TicketModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const { data: t } = useTicket(ticketId)
  const reply = useReplyTicket(ticketId)
  const [msg, setMsg] = useState('')

  const timeline = useMemo(() => {
    if (!t) return []
    const items = [
      {
        kind: 'opening' as const, id: 'opening', date: t.created_at,
        author: t.user_name || '—', is_admin: false, body: t.description,
        attachments: t.attachments || [],
      },
      ...t.replies.map(r => ({
        kind: 'reply' as const, id: r.id, date: r.created_at,
        author: r.user_name, is_admin: r.is_admin, body: r.message,
        attachments: r.attachments || [],
      })),
    ]
    return items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [t])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <span className="text-xs font-mono text-gray-500">#{t?.ticket_number}</span>
            <h3 className="font-semibold text-lg">{t?.subject}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          {t && (
            <>
              <div className="flex flex-wrap gap-2 text-xs mb-4">
                <StatusBadge status={t.status} />
                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">{t.type}</span>
                {t.module && <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">{t.module}</span>}
                {t.sdp_ticket_id && <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">SDP #{t.sdp_ticket_id}</span>}
              </div>

              {/* Timeline cronológica: abertura + replies (anexos vão dentro de cada item) */}
              <div className="relative pl-7">
                <div className="absolute top-2 bottom-2 left-2 w-px bg-gray-200 dark:bg-gray-700" />
                <ol className="space-y-3">
                  {timeline.map((it) => {
                    const dotColor = it.kind === 'opening' ? 'bg-emerald-500' : it.is_admin ? 'bg-blue-500' : 'bg-gray-400'
                    const cardBg = it.kind === 'opening'
                      ? 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40'
                      : it.is_admin ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    return (
                      <li key={it.id} className="relative">
                        <span className={`absolute -left-[26px] top-3 w-3 h-3 rounded-full ring-4 ring-white dark:ring-gray-900 ${dotColor}`} />
                        <div className={`rounded-lg border p-3 ${cardBg}`}>
                          <div className="flex items-center justify-between mb-1 text-xs">
                            <span className="font-semibold">
                              {it.kind === 'opening' ? '⬩ Abertura · ' : ''}{it.author}{it.is_admin ? ' · suporte' : ''}
                            </span>
                            <span className="text-gray-500 font-mono text-[11px]">
                              {new Date(it.date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {looksLikeHtml(it.body) ? (
                            <div className="text-sm nd-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(it.body) }} />
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{it.body}</p>
                          )}
                          {it.attachments && it.attachments.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/10">
                              <ul className="flex flex-wrap gap-1.5">
                                {it.attachments.map(a => (
                                  <li key={a.id}>
                                    <a href={a.file_path} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/70 dark:bg-gray-700/50 text-xs text-blue-700 dark:text-blue-300 hover:underline">
                                      <Paperclip className="h-3 w-3" />{a.original_name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-gray-200 dark:border-gray-800">
          <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="Sua resposta..."
            rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
          <div className="flex justify-end mt-2">
            <button disabled={!msg.trim() || reply.isPending} onClick={async () => { await reply.mutateAsync(msg); setMsg('') }}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Responder
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ── My Melhorias ──
function MyMelhorias() {
  const { data, isLoading } = useMyImprovements()
  const list = data || []

  const byStatus = useMemo(() => {
    const acc: Record<string, NdImprovement[]> = {}
    list.forEach(i => {
      acc[i.melhoria_status] = acc[i.melhoria_status] || []
      acc[i.melhoria_status].push(i)
    })
    return acc
  }, [list])

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <MelStat label="Em análise" count={byStatus.pending_analysis?.length || 0} color="bg-orange-500" />
        <MelStat label="Aprovadas" count={byStatus.approved?.length || 0} color="bg-emerald-500" />
        <MelStat label="Em desenvolvimento" count={byStatus.development?.length || 0} color="bg-blue-500" />
        <MelStat label="Publicadas" count={byStatus.published?.length || 0} color="bg-purple-500" />
        <MelStat label="Rejeitadas" count={byStatus.rejected?.length || 0} color="bg-red-500" />
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Assunto</th>
              <th className="px-4 py-3 text-left">Módulo</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Publicação</th>
              <th className="px-4 py-3 text-left">Criado</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {list.map(m => (
              <tr key={m.ticket_id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-4 py-3 font-mono text-gray-500">#{m.ticket_number}</td>
                <td className="px-4 py-3 max-w-md truncate">{m.subject}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{m.module || '—'}</td>
                <td className="px-4 py-3"><MelStatusBadge status={m.melhoria_status} /></td>
                <td className="px-4 py-3 text-xs">{m.actual_publish_date || m.planned_publish_date || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(m.created_at).toLocaleDateString('pt-BR')}</td>
              </tr>
            ))}
            {list.length === 0 && !isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nenhuma solicitação de melhoria.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MelStat({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <div className={`w-8 h-1 rounded-full ${color} mb-2`} />
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  )
}

export function MelStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending_analysis: 'bg-orange-100 text-orange-700',
    approved: 'bg-emerald-100 text-emerald-700',
    development: 'bg-blue-100 text-blue-700',
    published: 'bg-purple-100 text-purple-700',
    rejected: 'bg-red-100 text-red-700',
  }
  const labels: Record<string, string> = {
    pending_analysis: 'Analisando', approved: 'Aprovada',
    development: 'Desenvolvimento', published: 'Publicada', rejected: 'Rejeitada',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{labels[status] || status}</span>
}

// ── FAQ ──
function FaqTab() {
  const [q, setQ] = useState('')
  const { data: cats } = useFaq()
  const { data: search } = useFaqSearch(q)
  const showing = q.length >= 2

  return (
    <div>
      <div className="relative mb-6 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar na FAQ..."
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
      </div>

      {showing ? (
        <div className="space-y-3">
          {(search || []).map(item => (
            <details key={item.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <summary className="font-semibold cursor-pointer">{item.question}</summary>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 prose-sm" dangerouslySetInnerHTML={{ __html: item.answer }} />
            </details>
          ))}
          {search && search.length === 0 && <p className="text-sm text-gray-500">Nada encontrado.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {(cats || []).map(cat => (
            <section key={cat.id}>
              <h2 className="font-semibold mb-3 text-gray-900 dark:text-white">{cat.name}</h2>
              <div className="space-y-2">
                {cat.items.map(item => (
                  <details key={item.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                    <summary className="font-medium cursor-pointer">{item.question}</summary>
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 prose-sm" dangerouslySetInnerHTML={{ __html: item.answer }} />
                  </details>
                ))}
              </div>
            </section>
          ))}
          {(!cats || cats.length === 0) && <p className="text-sm text-gray-500">Nenhuma categoria de FAQ cadastrada.</p>}
        </div>
      )}
    </div>
  )
}
